/* =============================================================================
   CORE / STOCK — Lager / Bestandsführung (Phase E1)
   =============================================================================
   PURE Helfer für die Lager-Sicht. Bestand in BASISEINHEITEN (g/ml/Stück) je Zutat,
   konsistent mit Food-Cost (qtyInBaseUnits) — NICHT Stück→Gramm. Der aktuelle Bestand
   ist die Summe der signierten Ledger-Bewegungen (Single-Source, Spiegel der SQL-RPCs
   `ingredient_stock` / `stock_overview` aus Migration phase_118).

   Vorzeichen-Konvention (wie record_stock_movement / record_inventory):
     purchase    + (Zugang)
     consumption − (Verkauf-Abzug, E2)
     waste       − (Schwund)
     correction  ± (manuelle Korrektur)
     inventory   = Delta auf den gezählten Wert (Σ bleibt = gezählter Bestand)
============================================================================ */

/** Vorzeichen-Richtung eines Bewegungstyps (für die manuelle Eingabe-Maske). */
export const MOVEMENT_TYPES = [
  { type: 'purchase',   label: 'Wareneingang', sign: +1 },
  { type: 'correction', label: 'Korrektur',    sign: 0  }, // ± frei
  { type: 'waste',      label: 'Schwund',      sign: -1 },
  { type: 'inventory',  label: 'Inventur',     sign: 0  }  // Absolutwert → Delta serverseitig
];

/** Aktueller Bestand = Σ signierte Mengen. */
export function aggregateStock(movements) {
  return (Array.isArray(movements) ? movements : [])
    .reduce((s, m) => s + (m && isFinite(Number(m.quantityBase)) ? Number(m.quantityBase) : 0), 0);
}

/**
 * Bestands-Ampel: 'low' (≤ Mindestbestand), 'unknown' (kein Mindestbestand gesetzt),
 * sonst 'ok'. minStock == null → 'unknown' (kein Alarm definiert).
 */
export function stockBand(stock, minStock) {
  if (minStock == null || !isFinite(Number(minStock))) return 'unknown';
  return Number(stock) <= Number(minStock) ? 'low' : 'ok';
}

/**
 * Signierte Bewegungsmenge aus Typ + (positiver) Magnitude für die Eingabe-Maske.
 * purchase → +mag; waste → −mag; correction → wie eingegeben (Vorzeichen erlaubt).
 * Inventur läuft NICHT hierüber (record_inventory bekommt den Absolutwert).
 */
export function signedQuantity(type, magnitude) {
  const m = Number(magnitude);
  if (!isFinite(m)) return null;
  if (type === 'purchase') return Math.abs(m);
  if (type === 'waste') return -Math.abs(m);
  if (type === 'correction') return m; // ± erlaubt
  return null;
}

/** Bestand formatieren mit Basiseinheit-Suffix. null/NaN → „—". */
export function formatStock(n, baseUnit) {
  if (n == null || !isFinite(Number(n))) return '—';
  const unit = baseUnit === 'piece' ? 'Stk' : (baseUnit || '');
  // ganzzahlig ohne Nachkomma, sonst bis 2 NK
  const v = Number(n);
  const num = Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/0+$/, '').replace(/\.$/, '').replace('.', ',');
  return unit ? `${num} ${unit}` : num;
}

/** Zähl-Differenz für die Inventur-Vorschau (gezählt − aktuell). */
export function inventoryDelta(counted, current) {
  if (counted == null || !isFinite(Number(counted))) return null;
  return Number(counted) - Number(current || 0);
}

/**
 * E2 — Label einer Ledger-Bewegung für den Verlauf. Auto-Bewegungen (type
 * 'consumption') werden über `source` unterschieden: 'sale' = Verkauf-Abzug,
 * 'reversal' = Storno-Rückbuchung (manuelle Korrektur). Sonst der manuelle Typ.
 */
export function movementLabel(type, source) {
  if (type === 'consumption') {
    return source === 'reversal' ? 'Storno-Rückbuchung' : 'Verkauf';
  }
  // E4b — Wareneingang gegen Bestellung (source='order') lesbar kennzeichnen.
  if (type === 'purchase' && source === 'order') return 'Wareneingang (Bestellung)';
  const t = MOVEMENT_TYPES.find(x => x.type === type);
  return t ? t.label : type;
}

/** E2 — Bestand negativ? (Anzeige-Helfer; Server liefert is_negative ohnehin.) */
export function isNegativeStock(stock) {
  return Number(stock) < 0;
}

// ----------------------------------------------------------------------------
// E3 — Lagerwert / Inventarwert (phase_120)
// ----------------------------------------------------------------------------
// Zwei getrennte Werte je Zutat (vom Server geliefert): value_raw (Lagerwert, roher
// Einkaufspreis × Bestand, OHNE Yield, PRIMÄR) und value_yield (Nutzwert, yield-bereinigt,
// SEKUNDÄR). Fehlt der Einkauf → NULL → „nicht bewertet" (kein 0 €). Negativbestand →
// negativer Wert. Die exakten Summen liefert stock_value_totals(); die folgenden Helfer
// spiegeln das clientseitig (Anzeige/Tests, Fallback).

import { formatEuro } from './food-cost.js';

/** €-Wert formatieren (deutsch, 2 NK). null/undefined/NaN → „—" (nicht 0 €). */
export function formatStockValue(n) {
  if (n == null || !isFinite(Number(n))) return '—';
  return formatEuro(Number(n));
}

/**
 * Σ eines Wert-Feldes (valueRaw|valueYield) über Overview-Zeilen; NULL/—-Zeilen zählen 0.
 * Negativwerte fließen vorzeichenrichtig ein (Fehlmenge). Spiegel von stock_value_totals.
 */
export function aggregateStockValue(rows, key = 'valueRaw') {
  return (Array.isArray(rows) ? rows : []).reduce((s, r) => {
    const v = r && r[key];
    return s + (v != null && isFinite(Number(v)) ? Number(v) : 0);
  }, 0);
}

/** Anzahl Zutaten ohne Einkaufspreis (valueRaw == null) → „nicht bewertet". */
export function countUnvalued(rows) {
  return (Array.isArray(rows) ? rows : []).filter(r => !r || r.valueRaw == null).length;
}

/** Wert ist negativ? (für die rote Markierung; konsistent zu isNegativeStock). */
export function isNegativeValue(n) {
  return n != null && isFinite(Number(n)) && Number(n) < 0;
}
